import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import { useParams } from "react-router-dom";
import PageLoadingSpinner from "components/common/PageLoader";
import BreadCrumb from "components/common/BreadCrumb";
import { getDocumentTitle } from "utils/helpers";
import { getSupplier } from "../services/SupplierService";
import type { Supplier } from "../types/supplier.types";
import SupplierOverviewContext from "./SupplierOverviewContext";
import Section from "./Section";

const breadcrumbItems = [
  { label: "Suppliers", path: "/inventory-management/suppliers" },
];

const SupplierOverview = () => {
  document.title = getDocumentTitle("Supplier Details");
  const { id } = useParams();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getSupplier(Number(id));
      if (res.success && res.data) setSupplier(res.data);
      else setSupplier(null);
    } catch {
      setSupplier(null);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      await refetch();
      if (!cancelled) setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, refetch]);

  if (loading) return <PageLoadingSpinner message="Loading supplier…" />;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Supplier Details"
          pageTitle="Inventory Management"
          items={breadcrumbItems}
        />
        <SupplierOverviewContext.Provider value={{ supplier, refetch }}>
          <Section />
        </SupplierOverviewContext.Provider>
      </Container>
    </div>
  );
};

export default SupplierOverview;
